// FichajeFormEmpleado.jsx
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import api from "../../utils/Api";
import Clook from "../../components/Clock";

export default function FichajeFormEmpleado() {
  const { token, loading, user } = useContext(AuthContext);
  const [fichajes, setFichajes] = useState([]);
  const [generalError, setGeneralError] = useState("");
  const [notification, setNotification] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  // Modal incidencia
  const [modalOpen, setModalOpen] = useState(false);
  const [incidenciaComentario, setIncidenciaComentario] = useState("");
  const [incidenciaTipo, setIncidenciaTipo] = useState("incidencia");
  const [incidenciaFichajeId, setIncidenciaFichajeId] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  // Inicializar filtro por hoy
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setFilterDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  const parseDateTimeLocal = (fecha, hora) => {
    if (!fecha) return null;
    const dtStr = hora ? `${fecha}T${hora}` : `${fecha}T00:00:00`;
    const dt = new Date(dtStr);
    return isNaN(dt.getTime()) ? null : dt;
  };
  
  const hmsASegundos = (hms) => {
    const [h, m, s] = hms.split(":").map(Number);
    return h * 3600 + m * 60 + s;
  };

  // Función que utilizo para calcular el total de duración. 
  const segundosAHMS = (segundos) => {
    const h = Math.floor(segundos / 3600);
    const m = Math.floor((segundos % 3600) / 60);
    const s = segundos % 60;
    return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
  };

  const loadFichajes = () => {
    return api
      .get("/fichajes/mios", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setFichajes(res.data.data || []);
        return res.data.data || [];
      })
      .catch(() => {
        setGeneralError("Error al cargar los fichajes.");
        return [];
      });
  };

  useEffect(() => {
    if (!token) return;
    loadFichajes();
  }, [token]);

  const getAddress = async (lat, lon) => {
    try {
      const res = await api.get(
        "https://nominatim.openstreetmap.org/reverse",
        { params: { lat, lon, format: "json" }, headers: { "Accept-Language": "es" } }
      );
      const addr = res.data.address || {};
      return {
        ubicacion: addr.road || addr.neighbourhood || "Desconocida",
        ciudad: addr.city || addr.town || addr.village || "Desconocida",
      };
    } catch {
      return { ubicacion: "Desconocida", ciudad: "Desconocida" };
    }
  };

  const hoy = new Date().toISOString().split("T")[0];

  const ultimaEntrada = [...fichajes]
    .filter(f => f.tipo === "entrada")
    .reverse()
    .find(f => {
      const salidaPosterior = fichajes.find(s =>
        s.tipo === "salida" &&
        parseDateTimeLocal(s.fecha, s.hora_salida) >
        parseDateTimeLocal(f.fecha, f.hora_entrada)
      );
      return !salidaPosterior;
    });

  const ultimoDescanso = [...fichajes]
    .filter(f => f.tipo === "descanso")
    .reverse()
    .find(f => !f.hora_fin_descanso);

  // Detectar fichajes activos
  // const ultimaEntrada = [...fichajes]
  //   .filter(f => f.tipo === "entrada")
  //   .reverse()
  //   .find(f => {
  //     const salidaPosterior = fichajes.find(s =>
  //       s.tipo === "salida" &&
  //       s.fecha === f.fecha &&
  //       parseDateTimeLocal(s.fecha, s.hora_salida) > parseDateTimeLocal(f.fecha, f.hora_entrada)
  //     );
  //     return !salidaPosterior;
  //   });

  // const ultimoDescanso = [...fichajes]
  //   .filter(f => f.tipo === "descanso")
  //   .reverse()
  //   .find(f => {
  //     if (f.hora_fin_descanso) return false;
  //     const salidaPosterior = fichajes.find(s =>
  //       s.tipo === "salida" &&
  //       s.fecha === f.fecha &&
  //       parseDateTimeLocal(s.fecha, s.hora_salida) > parseDateTimeLocal(f.fecha, f.hora_entrada_descanso)
  //     );
  //     return !salidaPosterior;
  //   });

  const handleFichar = async (tipo) => {
    if (!token) return setNotification("Debes iniciar sesión primero");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const { ubicacion, ciudad } = await getAddress(latitude, longitude);

        let endpoint = "";

        if (tipo === "entrada") endpoint = "/fichajes/startEntrada";
        else if (tipo === "descanso") {
          if (!ultimaEntrada) return setNotification("No puedes iniciar descanso sin una entrada abierta.");
          endpoint = ultimoDescanso ? "/fichajes/endDescanso" : "/fichajes/startDescanso";
        } else if (tipo === "salida") {
          if (!ultimaEntrada) return setNotification("No puedes fichar salida sin una entrada abierta.");
          if (ultimoDescanso) return setNotification("Cierra el descanso antes de fichar salida.");
          endpoint = "/fichajes/salida";
        }

        api.post(endpoint,
          { ubicacion, ciudad, latitud: latitude, longitud: longitude, usuario_id: user.id, empresa_id: user.empresa_id },
          { headers: { Authorization: `Bearer ${token}` } }
        ).then(async () => {
          await loadFichajes();
          setPage(1);
          setNotification(`Fichaje de ${tipo} registrado`);
          setTimeout(() => setNotification(""), 4000);
        }).catch(err =>
          setGeneralError(err.response?.data?.error || "Error al registrar el fichaje.")
        );
      },
      () => setNotification("No se pudo obtener tu ubicación.")
    );
  };

  // Modal incidencia
  const openModal = () => {
    setIncidenciaComentario("");
    setIncidenciaTipo("incidencia");
    setIncidenciaFichajeId("");
    setValidationErrors({});
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleCrearIncidencia = async () => {
    if (!token) return setNotification("Debes iniciar sesión primero");
    if (!incidenciaComentario.trim()) return setNotification("Debes escribir un comentario");

    try {
      setValidationErrors({});
      await api.post(
        "/incidencias",
        { 
          comentario: incidenciaComentario, 
          tipo: incidenciaTipo,
          fichaje_id: incidenciaFichajeId || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotification("Incidencia creada correctamente");
      setTimeout(() => setNotification(""), 4000);
      closeModal();
      loadFichajes();

    } catch (err) {
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
      } else {
        setGeneralError(err.response?.data?.error || "Error al crear la incidencia.");
      }
    }
  };

  if (loading) return <p className="text-center mt-5">Cargando sesión...</p>;

  const fichajesFiltrados = filterDate ? fichajes.filter(f => f.fecha === filterDate) : fichajes;
  const paginatedFichajes = fichajesFiltrados.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.ceil(fichajesFiltrados.length / itemsPerPage);

  const totalSegundosUI = fichajes
    .filter(f => f.duracion !== null)
    .reduce((acc, f) => acc + f.duracion, 0);

  const totalHorasUI = segundosAHMS(totalSegundosUI);

  return (
    <div className="min-vh-100 d-flex flex-column">
      <header className="py-4 text-center shadow" style={{ backgroundColor: "#FF7A00", color: "#fff" }}>
        <h1 className="fs-3 fw-bold">Control de Fichajes</h1>
        <Clook />
      </header>

      <main className="container-fluid my-4 flex-grow-1">
        {notification && <div className="alert alert-info text-center">{notification}</div>}
        {generalError && <div className="alert alert-danger text-center">{generalError}</div>}

        {/* Filtro fecha */}
        <div className="mb-4 d-flex justify-content-center flex-wrap gap-2">
          <input type="date" value={filterDate} onChange={e => { setFilterDate(e.target.value); setPage(1); }} className="form-control w-auto" />
          <button className="btn" style={{ backgroundColor: "#2C2C2C", color: "#fff" }} onClick={() => setFilterDate("")}>Limpiar filtro</button>
          <button className="btn btn-warning" onClick={openModal}>Nueva Incidencia</button>
        </div>

        {/* Botones fichaje */}
        <div className="row g-3 justify-content-center mb-4">
          {["entrada", "descanso", "salida"].map(tipo => {
            const isActive =
              (tipo === "entrada" && ultimaEntrada) ||
              (tipo === "descanso" && ultimoDescanso);

            return (
              <div key={tipo} className="col-12 col-sm-6 col-md-4">
                <div className={`card shadow-sm rounded-4 p-3 p-md-4 text-center h-100 ${isActive ? "border border-3 border-success" : ""}`}>
                  <i className={`bi ${tipo === "entrada" ? "bi-door-open-fill" : tipo === "descanso" ? "bi-cup-straw" : "bi-door-closed-fill"} display-3 mb-3`} style={{ color: tipo === "entrada" ? "#FF7A00" : tipo === "descanso" ? "#FFC107" : "#DC3545" }}></i>
                  <h2 className="h5 mb-2">
                    {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                    {isActive && <span className="badge bg-success ms-2">Activo</span>}
                  </h2>
                  <p className="text-muted mb-3 small">
                    {tipo === "entrada" ? "Hora de entrada" : tipo === "descanso" ? "Iniciar/Finalizar descanso" : "Hora de salida"}
                  </p>
                  <button className="btn w-100 py-2" style={{ backgroundColor: tipo === "entrada" ? "#FF7A00" : tipo === "descanso" ? "#FFC107" : "#DC3545", color: "#fff" }} onClick={() => handleFichar(tipo)}>
                    <i className={`bi ${tipo === "entrada" ? "bi-door-open" : tipo === "descanso" ? "bi-cup-straw" : "bi-door-closed"} me-2`}></i>
                    {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <p>
          <strong>Total horas: {totalHorasUI}</strong>
        </p>
        {/* Tabla fichajes */}
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Ciudad</th>
                <th>Duración</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {paginatedFichajes.length > 0 ? (
                paginatedFichajes.map(f => {
                  const dt = f.tipo === "entrada" ? parseDateTimeLocal(f.fecha, f.hora_entrada)
                    : f.tipo === "salida" ? parseDateTimeLocal(f.fecha, f.hora_salida)
                    : parseDateTimeLocal(f.fecha, f.hora_entrada_descanso);

                  const fechaFormateada = dt ? dt.toLocaleDateString("es-ES") : "";
                  const horaFormateada = dt ? dt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";

                  const estado = f.tipo === "entrada" && f.hora_salida ? "Registrado"
                    : f.tipo === "descanso" && f.hora_fin_descanso ? "Desacanso finalizado"
                    : "";

                  return (
                    <tr key={f.id}>
                      <td className="text-capitalize">{f.tipo}</td>
                      <td>{fechaFormateada}</td>
                      <td>{horaFormateada}</td>
                      <td>{f.ciudad || "Desconocida"}</td>
                      <td>{f.duracion ? segundosAHMS(f.duracion) : ""}</td>
                      <td>{estado}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="text-center text-muted">No hay fichajes</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 10 }}>
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>Anterior</button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              style={{
                fontWeight: page === i + 1 ? "bold" : "normal",
                textDecoration: page === i + 1 ? "underline" : "none"
              }}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Siguiente</button>
        </div>
      )}
      
        {/* Modal incidencia */}
        {modalOpen && (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content rounded-4">
                <div className="modal-header">
                  <h5 className="modal-title">Nueva Incidencia</h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <div className="modal-body">
                  {Object.keys(validationErrors).length > 0 && 
                    <div className="alert alert-danger">
                      {Object.values(validationErrors).map((msgs, i) => <div key={i}>{msgs.join(", ")}</div>)}
                    </div>
                  }
                  <div className="mb-3">
                    <label className="form-label">Tipo de incidencia</label>
                    <select className="form-select" value={incidenciaTipo} onChange={e => setIncidenciaTipo(e.target.value)}>
                      <option value="incidencia">Incidencia</option>
                      <option value="falta">Falta</option>
                      <option value="retraso">Retraso</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Asociar a un fichaje (opcional)</label>
                    <select className="form-select" value={incidenciaFichajeId} onChange={e => setIncidenciaFichajeId(e.target.value)}>
                      <option value="">-- No asociar --</option>
                      {fichajes.map(f => (
                        <option key={f.id} value={f.id}>
                          {f.tipo} - {f.fecha} {f.hora_entrada || f.hora_salida || f.hora_entrada_descanso}
                        </option>
                      ))}
                    </select>
                  </div>
                  <textarea className="form-control" rows="5" placeholder="Describe la incidencia..." value={incidenciaComentario} onChange={e => setIncidenciaComentario(e.target.value)}></textarea>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                  <button className="btn btn-warning" onClick={handleCrearIncidencia}>Crear</button>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
